<script lang="ts">
  import moment from 'moment';
  import { onDestroy, onMount } from 'svelte';
  import {
    Chart as ChartJS,
    Tooltip,
    Legend,
    BarElement,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    LineController,
    BarController,
  } from 'chart.js';
  import { Chart } from 'svelte-chartjs';
  import zoomPlugin from 'chartjs-plugin-zoom';
  import loader from 'src/components/cabinet/#assets/loader.svg';
  import { getTransactionsAnalytics } from 'src/components/cabinet/#services/transactionsAnalytics';
  import Table from '../Table.svelte';
  import { options } from '../../configs/BarOptions';
  import DateRange from '../common/DateRange.svelte';
  import GameSelect from '../common/GameSelect.svelte';
  import { squezzeBranches } from '../../utils/branches';
  import { requestsParams } from '../../configs/Requests';
  import CountriesSelect from '../common/CountriesSelect.svelte';
  import { processChartDataset, resetChart } from '../../utils/common';
  import { getMathRewardsAnalytics } from '../../../#services/mathRewardsAnalytics';
  import { getGooglePlayAnalytics } from '../../../#services/googlePlayAnalytics';

  let chart, table;

  let creator = 'all';
  let category = 'purchases';
  let country = null;
  let userId = null;
  
  let labels = [];
  let datasets = [];

  let data = { labels, datasets };

  let startDate = 'startDate'
  let endDate = 'endDate'

  const minDate = '2023-01-01';
  const maxDate = moment().format('YYYY-MM-DD');

  ChartJS.register(
    LinearScale,
    CategoryScale,
    BarElement,
    PointElement,
    LineElement,
    Legend,
    Tooltip,
    LineController,
    BarController,
    zoomPlugin,
  );

  function refreshPurchases() {
    const requestData = requestsParams.status.hard({
      creator: creator !== 'all' ? creator : undefined,
      country: country?.value,
      userId: userId || undefined,
      startDate: startDate && startDate !== 'startDate' ? startDate : minDate,
      endDate: endDate && endDate !== 'endDate' ? endDate : maxDate,
    });

    getTransactionsAnalytics.send({ data: requestData });
  }

  function refreshRewards() {
    const requestData = requestsParams.status.rewards({
      game: creator !== 'all' ? creator : undefined,
      userId: userId || undefined,
      startDate: startDate && startDate !== 'startDate' ? startDate : minDate,
      endDate: endDate && endDate !== 'endDate' ? endDate : maxDate,
    });

    getMathRewardsAnalytics.send({ data: requestData });
  }

  function refreshFromGP() {
    const requestData = requestsParams.status.googlePlay({
      game: creator !== 'all' ? creator : undefined,
      country: country?.value,
      startDate: startDate && startDate !== 'startDate' ? startDate : minDate,
      endDate: endDate && endDate !== 'endDate' ? endDate : maxDate,
    });

    getGooglePlayAnalytics.send({ data: requestData });
  }

  function refresh() {
    resetChart(chart, labels, datasets);
      
    getTransactionsAnalytics.reset();
    getMathRewardsAnalytics.reset();
    getGooglePlayAnalytics.reset();
    
    if (category === 'purchases') {
      table = [];

      refreshPurchases();
      refreshFromGP();
    } else if (category === 'rewards') {
      refreshRewards();
    }
  }

  getTransactionsAnalytics.subscribe((value) => {
    if (value.hasValue && !value.error && !value.loading) {
      processChartDataset(value.value, labels, datasets, true);

      try {
        table.unshift(['Silver, LIS', value.value.branches[0]?.values], ['Gold, LIS', value.value.branches[1]?.values], ['Platinum, LIS', value.value.branches[2]?.values]);
      } catch (e) {}
    }
  });

  getGooglePlayAnalytics.subscribe((value) => {
    if (value.hasValue && !value.error && !value.loading) {
      processChartDataset(value.value, labels, datasets, false, 3);

      try {
        table.push(['From GP, LIS', value.value.branches[0].values]);
      } catch (e) {}
    }
  });

  getMathRewardsAnalytics.subscribe((value) => {
    if (value.hasValue && !value.error && !value.loading) {
      processChartDataset(value.value, labels, datasets, false, 0);

      try {
        table = [['Iron lootboxes, LIS', value.value.branches[0]?.values], ['Gold lootboxes, LIS', value.value.branches[1]?.values], ['Platinum lootboxes, LIS', value.value.branches[2]?.values]];
      } catch (e) {}
    }
  });

  onDestroy(() => {
    resetChart(chart);
    getTransactionsAnalytics.reset();
    getMathRewardsAnalytics.reset();
    getGooglePlayAnalytics.reset();
  });

  onMount(() => {
    refresh();
  });
</script>

<div class="buttons">
  <DateRange bind:startDate={startDate} bind:endDate={endDate} {minDate} {maxDate} refreshCallback={() => refresh()} />
</div>

<hr>

<div class="buttons">
  <div class="pc-line">
    <span class="date-label">Game</span>
    <GameSelect bind:creator={creator} refreshCallback={() => refresh()} />
  </div>
  <div class="pc-line">
    <span class="date-label">Category</span>
    <select class="form-select" bind:value={category} on:change={refresh}>
      <option value="purchases">Spents</option>
      <option value="rewards">Rewards</option>
    </select>
  </div>
  <div class="pc-line">
    <span class="date-label">User</span>
    <input type="text" class="form-control" bind:value={userId} placeholder="Input UserID" on:input={refresh}>
  </div>
  <div class="pc-line">
    <CountriesSelect bind:country={country} refreshCallback={() => refresh()} />
  </div>
</div>
<hr>
<div class="buttons">
  <div class="pc-line">
    <button class="btn btn-primary btn-filter" on:click={refresh}>Refresh</button>
  </div>
</div>
<p class="description">Summarized purchases of memberships from website and mobile apps (converted from dollars to lis according to the approximate rate) in LIS. Also you can find here information about rewards from lootboxes, that users gets by compliting daily quests. This information is not exact, but calculated analytically.</p>
<div class="graphic">
  {#if $getTransactionsAnalytics.error || $getGooglePlayAnalytics.error || $getMathRewardsAnalytics.error}
    Something went wrong. Try refreshing the page.
  {:else if $getTransactionsAnalytics.loading || $getGooglePlayAnalytics.loading || $getMathRewardsAnalytics.loading}
    <div class="graphic-loading">
      <img src="{loader}" alt="">
    </div>
  {:else if ($getTransactionsAnalytics.value && $getGooglePlayAnalytics.value) || $getMathRewardsAnalytics.value}
    <Chart bind:this={chart} type="bar" width={500} height={270} {data} {options} />
    <Table branches={{
      ...Object.fromEntries(table),
      'Total spent, LIS': squezzeBranches(table.map((v) => v[1])),
    }} isTableHidden={false} percentsInTotal={true} />
  {/if}
</div>